import {
  ActionReducerMapBuilder,
  PayloadAction,
  createSlice,
} from '@reduxjs/toolkit';

import {
  getBlogDetailsById,
  getBlogPostsById,
  uploadBlogImage,
} from '../actions/blogPageActions';
import { toggleBlogActiveStatus, toggleBlogAllowedStatus } from '../../admin/actions/adminActions';

// * initial state
const initialState: BlogPageStateType = {
  uploadBlogDetails: {
    url: '',
    title: '',
    description: '',
    tags: [],
    imagePreview: '',
    id: undefined,
  },
  blogDetails: {
    id: 0,
    url: '',
    title: '',
    description: '',
    tags: [],
    image_blog_page: '',
    isInitialized: false,
    is_active: true,
    is_allowed: true,
  },

  blogPostsList: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
  blogPostsPagination: {
    page: 1,
    page_size: 10,
    total_count: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  },
  // Unified loading flag for blog posts
  blogPostsLoading: false,

  isBlogPageLoading: false,
  blogDetailErrorMessage: '',
  isBlogUploading: false,
};

// * create slice state
const blogPageSlice = createSlice({
  name: 'blogDetailStateSlice',
  initialState,
  reducers: {
    setCurrentUploadBlogDetail: (
      state: BlogPageStateType,
      action: PayloadAction<UploadBlogStateType>
    ) => {
      state.uploadBlogDetails = action.payload;
    },
    setCurrentBlogDetail: (
      state: BlogPageStateType,
      action: PayloadAction<BlogCard>
    ) => {
      state.blogDetails = action.payload;
    },
    resetBlogDetailState: () => initialState,
    updateBlogModerationStatus: (
      state: BlogPageStateType,
      action: PayloadAction<BlogModerationUpdatePayload>
    ) => {
      if (state.blogDetails) {
        state.blogDetails.is_active = action.payload.is_active;
        state.blogDetails.is_allowed = action.payload.is_allowed;
      }
    },
  },
  extraReducers: builder => {
    blogDetails(builder);
    uploadBlogImageCases(builder);
    blogPosts(builder);
    adminModerationSuccessListener(builder);
  },
});

const blogDetails = (builder: ActionReducerMapBuilder<BlogPageStateType>) => {
  builder.addCase(getBlogDetailsById.pending, (state: BlogPageStateType) => {
    state.blogDetails = {
      id: 0,
      url: '',
      title: '',
      tags: [],
      description: '',
      image_blog_page: '',
      isLoading: true,
      isInitialized: false,
      is_active: true,
      is_allowed: true,
    };
  });

  builder.addCase(
    getBlogDetailsById.fulfilled,
    (state: BlogPageStateType, action: PayloadAction<BlogCard>) => {
      state.blogDetails = action.payload;
      state.blogDetails.isLoading = false;
      state.blogDetails.isInitialized = true;
    }
  );

  builder.addCase(
    getBlogDetailsById.rejected,
    (
      state: BlogPageStateType,
      action: PayloadAction<BlogPageDetailError | undefined>
    ) => {
      state.blogDetails = {
        ...state.blogDetails,
        isLoading: false,
      };
      state.blogDetailErrorMessage =
        action.payload?.detail || 'Unknown error occurred';
    }
  );
};

const blogPosts = (builder: ActionReducerMapBuilder<BlogPageStateType>) => {
  builder.addCase(getBlogPostsById.pending, (state: BlogPageStateType) => {
    // Use the consistent blogPostsLoading flag (not isBlogPageLoading)
    state.blogPostsLoading = true;
  });

  builder.addCase(
    getBlogPostsById.fulfilled,
    (
      state: BlogPageStateType,
      action: PayloadAction<{
        posts: BlogPostType[];
        pagination: {
          page: number;
          page_size: number;
          total_count: number;
          total_pages: number;
          has_next: boolean;
          has_previous: boolean;
        };
      }>
    ) => {
      state.blogPostsLoading = false;

      // Page 1 = fresh load (replace), any other page = load more (append)
      if (action.payload.pagination.page === 1) {
        state.blogPostsList.results = action.payload.posts;
      } else {
        state.blogPostsList.results = [
          ...state.blogPostsList.results,
          ...action.payload.posts,
        ];
      }

      state.blogPostsPagination = action.payload.pagination;
      state.blogPostsList.count = action.payload.pagination.total_count;
    }
  );

  builder.addCase(getBlogPostsById.rejected, (state: BlogPageStateType) => {
    state.blogPostsLoading = false;
    state.blogPostsList = { count: 0, next: '', previous: '', results: [] };
  });
};

const uploadBlogImageCases = (
  builder: ActionReducerMapBuilder<BlogPageStateType>
) => {
  builder.addCase(uploadBlogImage.pending, (state: BlogPageStateType) => {
    state.isBlogUploading = true;
  });
  builder.addCase(
    uploadBlogImage.fulfilled,
    (
      state: BlogPageStateType,
      action: PayloadAction<UploadBlogImageSuccessType>
    ) => {
      state.isBlogUploading = false;
      state.blogDetails.image_blog_page = action.payload.blogImageUrl;
    }
  );
  builder.addCase(
    uploadBlogImage.rejected,
    (
      state: BlogPageStateType,
      action: PayloadAction<UploadBlogImageErrorType | undefined>
    ) => {
      state.isBlogUploading = false;
    }
  );
  return builder;
};

const adminModerationSuccessListener = (
  builder: ActionReducerMapBuilder<BlogPageStateType>
) => {
  builder.addCase(toggleBlogActiveStatus.fulfilled, (state, action) => {
    if (state.blogDetails && state.blogDetails.id === action.meta.arg) {
      state.blogDetails.is_active = action.payload.is_active;
      state.blogDetails.is_allowed = action.payload.is_allowed;
    }
  });

  builder.addCase(toggleBlogAllowedStatus.fulfilled, (state, action) => {
    if (state.blogDetails && state.blogDetails.id === action.meta.arg) {
      state.blogDetails.is_active = action.payload.is_active;
      state.blogDetails.is_allowed = action.payload.is_allowed;
    }
  });
};

export const {
  setCurrentBlogDetail,
  setCurrentUploadBlogDetail,
  resetBlogDetailState,
} = blogPageSlice.actions;
export default blogPageSlice.reducer;